import { Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { Observable } from 'rxjs';
import { flatMap, map, take } from 'rxjs/operators';
import { v4 } from 'uuid';
import { onActions } from '../../utils/helpers/redux.helper';
import { ActionsEnum as FeaturesActionsEnum } from '../features-data/features-data.actions';
import { I2gMoneyContext } from './i2g-money.data';
import {
  ActionsEnum,
  fetchSettings,
  fetchStripeOnboardingUrl,
  resetEditingSettings,
  saveCurrentSettingsNetwork,
  saveEditingSettings,
  saveSettingsNetwork,
  SaveSettingsNetworkSuccess,
  setEditingSettingsToCurrent,
  unlinkPayPalAccount,
  updateBankTransfer,
  updateCardPaymentsSettings,
  updateCountry,
  updateCurrency,
  updateEditingSettings,
  updateInitialPaymentReceiptsSettings,
  updateLocale,
  updateMfaSettings,
  updatePaypalSettings,
  updateSettings,
  fetchI2gMoneyWebview,
  fetchI2gMoneyContext,
  updateI2gMoneyContext,
  openI2gMoneyOnboarding,
  closeI2gMoneyOnboarding,
  initializePlaid,
  submitPlaidLinkDetails,
  SubmitPlaidLinkDetailsPayload,
  startProcessing,
  finishProcessing,
  unlinkExternalBankAccount,
  fetchI2gMoneyAccountSummary,
  fetchI2gMoneyStatements,
} from './settings-data.actions';
import { SettingsDataStore } from './settings-data.store';
import { Settings } from './settings.model';

@Injectable()
export class SettingsDataDispatcher {
  constructor(
    private _store: Store<any>,
    private _actions: Actions,
    private _settingsDataStore: SettingsDataStore
  ) {}

  fetchSettings(
    shouldUpdateEditingSettings: boolean = false
  ): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(
      fetchSettings(dispatchId, shouldUpdateEditingSettings)
    );

    return onActions(
      this._actions,
      [ActionsEnum.FETCH_SETTINGS_SUCCESS, ActionsEnum.FETCH_SETTINGS_ERROR],
      dispatchId
    ).pipe(
      flatMap(() => this._settingsDataStore.getSettings()),
      take(1)
    );
  }

  saveSettingsNetwork(
    settings: Settings,
    dispatchId: string = v4()
  ): Observable<Settings> {
    this._store.dispatch(saveSettingsNetwork(dispatchId, settings));

    return onActions(
      this._actions,
      [
        ActionsEnum.SAVE_SETTINGS_NETWORK_SUCCESS,
        ActionsEnum.SAVE_SETTINGS_NETWORK_ERROR,
      ],
      dispatchId
    ).pipe(map(({ payload }: SaveSettingsNetworkSuccess) => payload.settings));
  }

  saveCurrentSettingsToNetwork(): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(saveCurrentSettingsNetwork(dispatchId));

    return onActions(
      this._actions,
      [
        ActionsEnum.SAVE_SETTINGS_NETWORK_SUCCESS,
        ActionsEnum.SAVE_SETTINGS_NETWORK_ERROR,
      ],
      dispatchId
    ).pipe(map(({ payload }: SaveSettingsNetworkSuccess) => payload.settings));
  }

  saveEditingSettings(): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(saveEditingSettings(dispatchId));

    return onActions(
      this._actions,
      [
        ActionsEnum.SAVE_SETTINGS_NETWORK_SUCCESS,
        ActionsEnum.SAVE_SETTINGS_NETWORK_ERROR,
      ],
      dispatchId
    ).pipe(map(({ payload }: SaveSettingsNetworkSuccess) => payload.settings));
  }

  updateSettings(
    settingsProps: Map<string, any>,
    resetOriginalEditingSettings: boolean = false,
    shouldUpdateEditingSettings: boolean = false
  ) {
    this._store.dispatch(
      updateSettings(
        settingsProps,
        resetOriginalEditingSettings,
        shouldUpdateEditingSettings
      )
    );
  }

  updateCountry(
    newCountry: string,
    shouldUpdateEditingSettings: boolean = false
  ): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(
      updateCountry(dispatchId, newCountry, shouldUpdateEditingSettings)
    );

    return onActions(
      this._actions,
      [
        FeaturesActionsEnum.FETCH_FEATURES_DATA_SUCCESS,
        FeaturesActionsEnum.FETCH_FEATURES_DATA_ERROR,
      ],
      dispatchId
    ).pipe(
      flatMap(() => this._settingsDataStore.getSettings()),
      take(1)
    );
  }

  updateCurrency(
    newCurrency: string,
    shouldUpdateEditingSettings: boolean = false
  ): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(
      updateCurrency(dispatchId, newCurrency, shouldUpdateEditingSettings)
    );

    return onActions(
      this._actions,
      [
        FeaturesActionsEnum.FETCH_FEATURES_DATA_SUCCESS,
        FeaturesActionsEnum.FETCH_FEATURES_DATA_ERROR,
      ],
      dispatchId
    ).pipe(
      flatMap(() => this._settingsDataStore.getSettings()),
      take(1)
    );
  }

  updateLocale(
    newLocale: string,
    shouldUpdateEditingSettings: boolean = false
  ): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(
      updateLocale(dispatchId, newLocale, shouldUpdateEditingSettings)
    );

    return onActions(
      this._actions,
      [
        FeaturesActionsEnum.FETCH_FEATURES_DATA_SUCCESS,
        FeaturesActionsEnum.FETCH_FEATURES_DATA_ERROR,
      ],
      dispatchId
    ).pipe(
      flatMap(() => this._settingsDataStore.getSettings()),
      take(1)
    );
  }

  updateBankTransfer(enabled: boolean, href: string): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(updateBankTransfer(dispatchId, enabled, href));

    return this._settingsDataStore
      .getSettingsDispatchedResponse(dispatchId)
      .pipe(map((entity: any) => entity as Settings));
  }

  updateCardPaymentsSettings(
    enabled: boolean,
    href: string
  ): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(updateCardPaymentsSettings(dispatchId, enabled, href));

    return this._settingsDataStore
      .getSettingsDispatchedResponse(dispatchId)
      .pipe(map((entity: any) => entity as Settings));
  }

  updatePaypalSettings(enabled: boolean): Observable<Settings> {
    const dispatchId = v4();

    this._store.dispatch(updatePaypalSettings(dispatchId, enabled));

    return this._settingsDataStore
      .getSettingsDispatchedResponse(dispatchId)
      .pipe(map((entity: any) => entity as Settings));
  }

  updateMfaSettings(enabled: boolean) {
    this._store.dispatch(updateMfaSettings(enabled));
  }

  updateInitialPaymentReceiptsSettings(enabled: boolean) {
    this._store.dispatch(updateInitialPaymentReceiptsSettings(enabled));
  }

  updateEditingSettings(settingsProps: Map<string, any>) {
    this._store.dispatch(updateEditingSettings(settingsProps));
  }

  resetEditingSettings() {
    this._store.dispatch(resetEditingSettings());
  }

  setEditingSettingsToCurrent() {
    this._store.dispatch(setEditingSettingsToCurrent());
  }

  fetchStripeOnboardingUrl(enableCardPayments: boolean, returnUrl: string) {
    this._store.dispatch(
      fetchStripeOnboardingUrl(enableCardPayments, returnUrl)
    );
  }

  fetchI2gMoneyWebview() {
    this._store.dispatch(fetchI2gMoneyWebview());
  }

  fetchI2gMoneyContext() {
    this._store.dispatch(fetchI2gMoneyContext());
  }

  updateI2gMoneyContext(
    enabled: boolean
  ): Observable<I2gMoneyContext['settings']> {
    this._store.dispatch(updateI2gMoneyContext(enabled));

    return onActions(this._actions, [
      ActionsEnum.UPDATE_I2G_MONEY_CONTEXT_SUCCESS,
    ]).pipe(
      flatMap(() => this._settingsDataStore.getI2gMoney()),
      take(1),
      map((value) => value.context.settings)
    );
  }

  showI2gMoneyOnboarding(trackingScreenName: string) {
    this._store.dispatch(openI2gMoneyOnboarding(trackingScreenName));
  }

  hideI2gMoneyOnboarding() {
    this._store.dispatch(closeI2gMoneyOnboarding());
  }

  initializePlaid() {
    this._store.dispatch(initializePlaid());
  }

  startProcessing() {
    this._store.dispatch(startProcessing());
  }

  finishProcessing() {
    this._store.dispatch(finishProcessing());
  }

  submitPlaidLinkDetails(linkDetails: SubmitPlaidLinkDetailsPayload) {
    this._store.dispatch(submitPlaidLinkDetails(linkDetails));
  }

  unlinkExternalBankAccount() {
    this._store.dispatch(unlinkExternalBankAccount());
  }

  getI2gMoneyAccountSummary() {
    this._store.dispatch(fetchI2gMoneyAccountSummary());
  }

  getI2gMoneyStatements() {
    this._store.dispatch(fetchI2gMoneyStatements());
  }

  unlinkPayPalAccount() {
    this._store.dispatch(unlinkPayPalAccount());
  }
}
